import type {
  IAuth,
  IAuthProfile,
  IAuthResponse,
  IAuthTenants,
  IChangePassword,
  ILogin,
  ILoginResponse,
  INotificationSettings,
  IOrganizationAuth,
  IOrganizationAuthResponse,
  IResendVerification,
  IResetPassword,
  ISignup,
  IUpdateAreas,
  IValidateSessionResponse,
  Profile
} from '@/interfaces/agency/auth';
import type { IArea } from '@/interfaces/agency/common';
import axios from '@axios';

export default {
  validateUserCredential(payload: IAuth) {
    return axios.post<IAuthResponse>('/auth/login', payload);
  },

  validateUserOrganisation(payload: IOrganizationAuth) {
    return axios.post<Array<IOrganizationAuthResponse>>('/auth/user-tenant-roles', payload);
  },

  login(payload: ILogin) {
    return axios.post<ILoginResponse>('/auth/generate-tokens', payload, {
      headers: { 'x-hide-loader': true }
    });
  },

  logout() {
    // Must run before tokens are cleared from localStorage so the request interceptor still
    // attaches a valid Authorization header. Marked to skip the refresh-retry interceptor so an
    // already-expired access token here can't trigger a refresh/logout loop.
    const logoutRequestConfig = {
      headers: { 'x-hide-loader': true },
      _skipInterceptor: true
    };

    return axios.post('/auth/logout', {}, logoutRequestConfig);
  },

  validateSession(refreshToken: string) {
    // Silent background check: no loader, no error toast, and no 401 refresh/logout handling —
    // the caller decides what an invalid or failed check means.
    const validateSessionConfig = {
      headers: { 'x-hide-loader': true, 'x-silent-error': 'true' },
      _skipInterceptor: true
    };

    return axios.post<IValidateSessionResponse>('/auth/validate-session', { refreshToken }, validateSessionConfig);
  },

  getUserProfile() {
    return axios.get<IAuthProfile>('/auth/profile', {
      headers: { 'x-hide-loader': true }
    });
  },

  requestReset(email: string) {
    return axios.post('/forgot-password/request-reset', { email });
  },

  verifyOtp(payload: { email: string; otp: string }) {
    return axios.post('/forgot-password/verify-otp', payload);
  },

  resetPassword(payload: IResetPassword) {
    return axios.post('/forgot-password/reset-password', payload);
  },

  changePassword(payload: IChangePassword) {
    return axios.post('/change-password', payload);
  },

  signup(payload: ISignup) {
    return axios.post('/signup', payload);
  },

  fetchAgencyDetails(profileId: number, hideLoader = false) {
    return axios.get<Profile>(`/profiles/${profileId}`, {
      headers: hideLoader ? { 'x-hide-loader': true } : {}
    });
  },

  updateAgencyDetails(profileId: number, payload: Partial<Profile>) {
    return axios.patch(`/profiles/${profileId}`, payload);
  },

  fetchAgencyNotificationSettings(profileId?: number) {
    return axios.get<Array<INotificationSettings>>('/notification-settings', { params: { profileId } });
  },

  updateAgencyNotificationSettings(payload: Partial<INotificationSettings>) {
    return axios.post('/notification-settings', payload);
  },

  fetchAgencyAreas(profileId: number, hideLoader = false) {
    return axios.get<Array<IArea>>(`/profile-areas/profiles/${profileId}`, {
      headers: hideLoader ? { 'x-hide-loader': true } : {}
    });
  },

  updateAgencyAreas(profileId: number, payload: IUpdateAreas) {
    return axios.patch(`/profile-areas/profiles/${profileId}`, payload);
  },

  emailVerification(query: object) {
    return axios.get('/auth/verify-email', { params: query });
  },

  resendVerification(payload: IResendVerification) {
    return axios.post('/auth/resend-verification', payload);
  },

  fetchUserTenantRoles(userId: number) {
    const params = { userId };

    return axios.get<Array<IAuthTenants>>('/auth/user-tenant-roles', { params });
  }
};
